import { useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MapPin, MessageCircle, Hotel, Video, HeartPulse, Syringe, Building2, Activity,
  ArrowUpRight, MessagesSquare, CalendarCheck, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/i18n/routing";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { LeadDialog } from "@/components/LeadDialog";
import { ContactChannels } from "@/components/ContactChannels";
import { EmergencyNotice } from "@/components/EmergencyNotice";
import { SafetyStrip } from "@/components/SafetyStrip";
import { LOCATIONS, CONTACT, waLink } from "@/data/site";
import { getLocationContent } from "@/data/locationContent";

const BASE = "https://doctorsvietnam.com";

const SERVICE_LINKS = [
  { icon: Hotel, label: "Doctor to Hotel", to: "/doctor-to-hotel-vietnam" },
  { icon: Video, label: "Online Doctor Consultation", to: "/online-doctor-consultation-vietnam" },
  { icon: Building2, label: "Clinic & Hospital Booking", to: "/clinic-hospital-booking-vietnam" },
  { icon: Activity, label: "Urgent Medical Support", to: "/urgent-medical-support-vietnam" },
  { icon: HeartPulse, label: "Nursing Care", to: "/nursing-care-vietnam" },
  { icon: Syringe, label: "Post-Operative Support", to: "/post-operative-nursing-vietnam" },
];

const LocationDetail = () => {
  const { t } = useTranslation();
  const { slug, lang } = useParams();
  const loc = LOCATIONS.find((l) => l.slug === slug);
  const content = getLocationContent(slug);
  if (!loc || !content) return <Navigate to="/locations" replace />;

  const others = LOCATIONS.filter((l) => l.slug !== slug);
  const city = loc.name;

  const REQUEST_STEPS = [
    { icon: MessagesSquare, title: t("steps.s1t"), text: t("steps.s1x") },
    { icon: Info, title: t("steps.s2t"), text: t("steps.s2x") },
    { icon: CalendarCheck, title: t("steps.s3t"), text: t("steps.s3x") },
  ];

  const medicalBusinessSchema = {
    "@context": "https://schema.org", "@type": "Organization", name: `Doctors Vietnam — ${city}`,
    description: content.metaDescription, url: `${BASE}/${lang}/locations/${loc.slug}`,
    email: CONTACT.email, telephone: `+${CONTACT.whatsappNumber}`,
    areaServed: { "@type": "City", name: city, containedInPlace: { "@type": "Country", name: "Vietnam" } },
    availableLanguage: CONTACT.languages,
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("nav.home"), item: `${BASE}/${lang}` },
      { "@type": "ListItem", position: 2, name: t("nav.locations"), item: `${BASE}/${lang}/locations` },
      { "@type": "ListItem", position: 3, name: city, item: `${BASE}/${lang}/locations/${loc.slug}` },
    ],
  };

  return (
    <>
      <Seo exact title={content.seoTitle} description={content.metaDescription} path={`/locations/${loc.slug}`} jsonLd={[medicalBusinessSchema, breadcrumbSchema]} />
      <PageHero eyebrow={loc.region} title={t("location.h1", { city })} subtitle={t("location.intro", { city })}
        crumbs={[{ label: t("nav.home"), to: "/" }, { label: t("nav.locations"), to: "/locations" }, { label: city }]} />

      <section className="vc-container py-14 sm:py-16">
        <Reveal>
          <div data-testid="availability-notice" className="flex items-start gap-3 rounded-2xl border border-[hsl(var(--vc-line))] bg-mint p-5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand text-white"><Info className="h-5 w-5" /></span>
            <p className="text-sm font-semibold leading-relaxed text-[hsl(var(--vc-ink))]">{t("notices.availability")}</p>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Reveal>
              <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">Is this service right for me?</h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                <li className="flex items-start gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-4 vc-shadow-soft">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-mint text-brand"><Info className="h-3.5 w-3.5" /></span>
                  <span className="text-sm text-[hsl(var(--vc-ink))]">You're staying in {city} and need help finding an English-speaking doctor or clinic.</span>
                </li>
                <li className="flex items-start gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-4 vc-shadow-soft">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-mint text-brand"><Info className="h-3.5 w-3.5" /></span>
                  <span className="text-sm text-[hsl(var(--vc-ink))]">You'd prefer a doctor to visit your hotel, apartment or home where available.</span>
                </li>
                <li className="flex items-start gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-4 vc-shadow-soft">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-mint text-brand"><Info className="h-3.5 w-3.5" /></span>
                  <span className="text-sm text-[hsl(var(--vc-ink))]">You want coordination for a clinic, hospital, specialist or nursing support in {city}.</span>
                </li>
                <li className="flex items-start gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-4 vc-shadow-soft">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-mint text-brand"><Info className="h-3.5 w-3.5" /></span>
                  <span className="text-sm text-[hsl(var(--vc-ink))]">You're a tourist, expat, business traveller or international patient.</span>
                </li>
              </ul>
            </Reveal>

            <Reveal className="mt-10">
              <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t("location.available", { city })}</h2>
              <p className="mt-2 text-sm text-[hsl(var(--vc-slate))]">{content.intro}</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {SERVICE_LINKS.map((s) => (
                  <Link key={s.to} to={s.to} data-testid={`city-service-${s.to.replace(/\//g, "")}`}
                    className="group flex items-center justify-between gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-4 vc-shadow-soft transition hover:border-brand/40 hover:vc-shadow-card">
                    <span className="flex items-center gap-3">
                      <span className="grid h-9 w-9 place-items-center rounded-lg bg-mint text-brand"><s.icon className="h-4 w-4" /></span>
                      <span className="text-sm font-semibold text-[hsl(var(--vc-ink))]">{s.label}</span>
                    </span>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-brand transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                ))}
              </div>
            </Reveal>

            <Reveal className="mt-10" data-testid={`location-reasons-${loc.slug}`}>
              <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t("location.commonReasons", { city })}</h2>
              <p className="mt-3 text-sm leading-relaxed text-[hsl(var(--vc-slate))]">
                {content.reasons || t("location.commonReasonsText")}
              </p>
            </Reveal>

            <Reveal className="mt-10">
              <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t("location.areas", { city })}</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {loc.areas.map((a) => (
                  <span key={a} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--vc-line))] bg-mint px-3.5 py-1.5 text-sm font-medium text-[hsl(var(--vc-ink))]">
                    <MapPin className="h-3.5 w-3.5 text-brand" /> {a}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal className="mt-10">
              <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t("location.howTo", { city })}</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                {REQUEST_STEPS.map((s, i) => (
                  <div key={i} className="rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-5 vc-shadow-soft">
                    <div className="flex items-center gap-2">
                      <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand text-white"><s.icon className="h-4 w-4" /></span>
                      <span className="text-sm font-bold text-brand">{i + 1}</span>
                    </div>
                    <h3 className="mt-3 text-base font-bold text-[hsl(var(--vc-ink))]">{s.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{s.text}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal className="mt-10"><EmergencyNotice /></Reveal>

            {content.faqs && content.faqs.length > 0 && (
              <Reveal className="mt-10">
                <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">Frequently asked questions</h2>
                <Accordion type="single" collapsible className="mt-4" data-testid={`location-faq-accordion-${loc.slug}`}>
                  {content.faqs.map((f, i) => (
                    <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-[hsl(var(--vc-line))]">
                      <AccordionTrigger className="text-left text-base font-bold text-[hsl(var(--vc-ink))] hover:no-underline" data-testid={`location-faq-trigger-${i}`}>
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm leading-relaxed text-[hsl(var(--vc-slate))]">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            )}

            <Reveal className="mt-10"><SafetyStrip /></Reveal>
          </div>

          <aside>
            <div className="lg:sticky lg:top-24 rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-6 vc-shadow-card">
              <h3 className="text-lg font-extrabold text-[hsl(var(--vc-ink))]">{t("location.requestTitle", { city })}</h3>
              <p className="mt-1.5 text-sm text-[hsl(var(--vc-slate))]">{t("location.requestText")}</p>
              <LeadDialog title={t("location.requestTitle", { city })} defaultType="medical" presetService={`Location: ${city}`} source={`location-${loc.slug}`}
                trigger={<Button className="mt-4 w-full bg-brand py-5 font-bold text-white hover:opacity-90" data-testid="location-request-btn">{t("cta.getMedical")}</Button>} />
              <Button asChild variant="outline" className="mt-3 w-full border-2 border-[hsl(var(--vc-line))] font-bold text-[hsl(var(--vc-ink))] hover:border-brand hover:bg-mint hover:text-brand">
                <a href={waLink(`Hello Doctors Vietnam, I need help finding a doctor in ${city}.`)} target="_blank" rel="noopener noreferrer" data-testid="location-whatsapp-btn">
                  <MessageCircle className="mr-2 h-4 w-4" /> {t("cta.whatsappUs")}
                </a>
              </Button>
              <ContactChannels variant="inline" className="mt-5 flex-col gap-3" />
            </div>
          </aside>
        </div>

        <div className="mt-16">
          <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t("location.others")}</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to={`/locations/${o.slug}`} data-testid={`related-city-${o.slug}`}
                className="group flex items-center justify-between gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-4 vc-shadow-soft transition hover:border-brand/40 hover:vc-shadow-card">
                <span className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-mint text-brand"><MapPin className="h-4 w-4" /></span>
                  <span className="flex flex-col">
                    <span className="text-sm font-semibold text-[hsl(var(--vc-ink))]">{o.name}</span>
                    <span className="text-xs text-[hsl(var(--vc-slate))]">{o.region}</span>
                  </span>
                </span>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-brand transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="vc-container pb-16 sm:pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-brand px-6 py-12 text-center text-white vc-shadow-card sm:px-10 sm:py-14">
          <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{t("location.ctaTitle", { city })}</h2>
          <Button asChild className="mt-7 bg-white py-6 text-base font-bold text-brand hover:bg-white/90" data-testid="location-cta-whatsapp">
            <a href={waLink(`Hello Doctors Vietnam, I need help finding a doctor in ${city}.`)} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="mr-2 h-5 w-5" /> {t("cta.chatWhatsapp")}
            </a>
          </Button>
          <p className="mt-5 text-xs text-white/70">{CONTACT.whatsappDisplay} · {CONTACT.email}</p>
        </div>
      </section>
    </>
  );
};

export default LocationDetail;
